/**
 * Triage: doing something to mail, and being able to take it back.
 *
 * TWO THINGS MEET HERE, and both were built before there was anything to
 * connect them. `model/optimistic.ts` has held the mutation queue — precomputed
 * inverses, per-flag deltas so two devices merge instead of clobbering, one
 * undo — since before the client existed. And `/ops` only started reaching the
 * mail server this week: it used to mutate the index alone and answer
 * `{ok: true}`, so a client could not have told a write from a no-op.
 *
 * THE OPTIMISTIC UPDATE IS APPLIED LOCALLY FIRST and rolled back on failure.
 * That is not for show: `interaction.md` budgets keystroke-to-paint at under
 * 100 ms, and the round trip to the origin is 39-43 ms on the tailnet before
 * IMAP does anything. Waiting for the server would make every `e` feel slow.
 *
 * A MOVE IS NOT UNDOABLE UNTIL THE SERVER SAYS WHERE IT LANDED, and that is the
 * subtle part. IMAP UIDs are per-mailbox, so the UID after a MOVE is not the one
 * before it, and only the UIDPLUS `COPYUID` response reveals the new one. It
 * arrives with the ack, after the optimistic update. `invert()` returns null
 * until then and `ack(id, landing)` supplies it — so the window where Undo is
 * offered but would move the WRONG message does not exist.
 */
import { useCallback, useRef, useState } from 'react'
import {
  api,
  HearthError,
  SNOOZE_SPECIAL_USE,
  SNOOZE_WAKE_MAILBOX,
  type MailboxState,
  type MessageRow,
  type Op,
} from './api.ts'
import { OperationQueue, type Operation, type ToastState } from './model/optimistic.ts'

export interface OpsOptions {
  readonly mailboxes: readonly MailboxState[]
  /** Patch the rows on screen. Called with a function so it composes with paging. */
  readonly patchRows: (patch: (rows: readonly MessageRow[]) => readonly MessageRow[]) => void
  readonly onError: (message: string) => void
}

export interface Triage {
  readonly toast: ToastState | null
  /** Add or remove IMAP flags on every target. */
  readonly flag: (rows: readonly MessageRow[], add: string[], remove: string[], label: string) => Promise<void>
  /** Move every target to the mailbox carrying this SPECIAL-USE flag. */
  readonly moveToSpecial: (rows: readonly MessageRow[], specialUse: string, label: string) => Promise<void>
  /**
   * Move every target into a mailbox chosen by NAME.
   *
   * SEPARATE FROM `moveToSpecial` BECAUSE THE QUESTION IS DIFFERENT, not because
   * the mechanism is. Archive, Trash and Junk are found by their SPECIAL-USE
   * flag — `shell-ia.md`'s rule is that no folder is special-cased by name, and
   * this household's Archive is called `Archiv` — while a person picking
   * `Finance/Bank` out of a list is naming a mailbox that has no flag and never
   * will. The name comes from the mailbox list the ORIGIN published, so it is
   * still never a string this client invented.
   */
  readonly moveTo: (rows: readonly MessageRow[], mailbox: string, label: string) => Promise<void>
  /**
   * Add or remove a label on every target.
   *
   * A LABEL IS AN IMAP KEYWORD AND THIS GOES THROUGH `flag`, which is the same
   * decision `replyLater` records and for the same reason. The origin has a
   * `label` op keyed by GUID that reaches every location a message sits in, and
   * it is the better op — but routing a second mutation path straight at
   * `api.ops` would lose the optimistic patch and the undo, which is precisely
   * what this hook exists to be the only one of. `message_keywords` is a UNION
   * over the locations, so `label:invoices` finds the message either way; the
   * difference is invisible to a reader and the undo is not.
   */
  readonly label: (rows: readonly MessageRow[], add: string[], remove: string[], text: string) => Promise<void>
  /**
   * Take every target off the screen until `until`, or bring it back with `null`.
   *
   * `until` IS UNIX SECONDS, an instant and not a duration — the origin refuses
   * a value in the past or more than ten years out, which is what catches both
   * `Date.now()` undivided and a duration sent where a moment was meant.
   *
   * IT IS UNDOABLE THROUGH THE ORDINARY UNDO, with no second mechanism: a snooze
   * is a move into the archive, the origin answers it with `moved` exactly as it
   * answers a move, and the inverse the queue builds — the move back — is also
   * what tells the origin the snooze is over. See `vanish`.
   *
   * WHICH MEANS UNDOING AN *UN*-SNOOZE PUTS THE MESSAGE BACK IN THE ARCHIVE AND
   * NOT BACK ON THE CLOCK, and that is the honest half of reusing the move: the
   * inverse of a move is a move, and a move is what ends a snooze. The message
   * goes back where it was; a reader who wants it parked again presses `b`.
   */
  readonly snooze: (rows: readonly MessageRow[], until: number | null, label: string) => Promise<void>
  readonly undo: () => Promise<void>
  readonly dismissToast: () => void
}

/**
 * Find a mailbox by its IMAP SPECIAL-USE flag, never by name.
 *
 * `\\Archive` is the flag; the folder behind it may be called Archive, Archiv,
 * Arkiv or All Mail. `shell-ia.md` states the rule — no folder is special-cased
 * by name — and it is not pedantry: this household's server is a stock
 * docker-mailserver whose owner can rename anything.
 */
function specialMailbox(mailboxes: readonly MailboxState[], specialUse: string): string | null {
  const found = mailboxes.find((box) => (box.specialUse ?? '').toLowerCase() === specialUse.toLowerCase())
  return found?.name ?? null
}

const flagsOf = (row: MessageRow): Set<string> => new Set(row.flags.trim().split(/\s+/).filter(Boolean))
/** Back to the wire's shape: one space-delimited string with a leading and trailing space. */
const flagsToText = (flags: Iterable<string>): string => {
  const list = [...flags]
  return list.length === 0 ? '' : ` ${list.join(' ')} `
}

/**
 * Put rows back where they were.
 *
 * A LIST ORDERED BY DATE HAS ONE CORRECT PLACE FOR A ROW, and it is not the
 * top. Reinstatement used to concatenate the snapshot in front of the current
 * rows, so a failed archive of a three-week-old message moved it to the front
 * of the inbox, where it reads as new mail — a failure that looks like an
 * arrival.
 *
 * The recorded index is where the row was WHEN THE ACTION HAPPENED, and the
 * list may have moved under it since. That makes this an approximation, and a
 * good one: it is exact in the common case (nothing else changed), and in the
 * uncommon one it is out by however many rows arrived, rather than by the
 * length of the list.
 */
export function reinstate(
  current: readonly MessageRow[],
  snapshot: ReadonlyMap<string, { row: MessageRow; index: number }>,
  restore: ReadonlySet<string>,
): readonly MessageRow[] {
  const back = [...snapshot.values()]
    .filter((entry) => restore.has(entry.row.id))
    .sort((a, b) => a.index - b.index)
  if (back.length === 0) return current
  const out = [...current]
  for (const entry of back) {
    // Already there — a second failure report for the same row, or a refresh
    // that brought it back — leaves the list alone rather than duplicating it.
    if (out.some((row) => row.id === entry.row.id)) continue
    out.splice(Math.min(entry.index, out.length), 0, entry.row)
  }
  return out
}

/**
 * How many batches' removed rows to keep for Undo.
 *
 * The undo window is ten seconds and only the most recent batch is offered, so
 * one would nearly do; a handful covers a reader who acts faster than the
 * network answers, and bounds a map that would otherwise grow all session.
 */
const MAX_REMEMBERED_BATCHES = 8

export function useOps(options: OpsOptions): Triage {
  const queue = useRef(new OperationQueue(() => Math.floor(Date.now() / 1000)))
  /**
   * What each batch took OFF the screen, so its Undo can put it back.
   *
   * Only moves remove rows; a flag change edits them in place and needs
   * nothing here. Keyed by batch and bounded by `MAX_REMEMBERED_BATCHES`,
   * because an undo window is ten seconds and a session is not.
   */
  const removed = useRef(new Map<string, { rowIds: string[]; snapshot: Map<string, { row: MessageRow; index: number }> }>())
  const [toast, setToast] = useState<ToastState | null>(null)
  const refreshToast = useCallback(() => setToast(queue.current.toast()), [])

  const send = useCallback(
    async (
      ops: Op[],
      operations: Operation[],
      label: string,
      /**
       * Put back the rows whose ops did NOT succeed.
       *
       * A SET, NOT A CLOSURE OVER EVERYTHING, and that is the fix rather than a
       * refactor: any failure used to roll the whole batch back while the
       * server kept the half that worked, so archiving twelve messages with one
       * refusal put all twelve back on screen and eleven of them were no longer
       * there. Passing which ones failed is what lets the view match the server.
       */
      rollback: (failedRowIds: ReadonlySet<string>) => void,
      /** Row id per op, positionally. Used to map a refusal back to a row. */
      rowIds: readonly string[],
      /** Called with the batch id before anything is sent, for a caller that has rows to remember. */
      onBatch?: (batchId: string) => void,
    ): Promise<void> => {
      const batch = queue.current.nextBatch()
      onBatch?.(batch)
      const pending = operations.map((operation) => queue.current.enqueue(operation, label, batch))
      refreshToast()
      try {
        const { results } = await api.ops(ops)
        const failed = results.filter((result) => !result.ok)
        if (failed.length > 0) {
          const failedIds = new Set(failed.map((result) => result.id))
          const failedRows = new Set(
            ops.map((op, index) => (failedIds.has(op.id) ? rowIds[index] : undefined)).filter((id): id is string => !!id),
          )
          rollback(failedRows)
          // ONLY THE FAILED ENTRIES are marked failed. The others really did
          // happen, and marking them failed would take them out of the undo the
          // reader is about to be offered for the part that worked.
          for (const [index, entry] of pending.entries()) {
            if (failedIds.has(ops[index]?.id ?? '')) queue.current.fail(entry.id)
            else {
              const result = results.find((r) => r.id === ops[index]?.id)
              queue.current.ack(entry.id, result?.moved ? { uid: result.moved.uid } : undefined)
            }
          }
          refreshToast()
          options.onError(
            failed.length === results.length
              ? (failed[0]!.error ?? 'The mail server refused that')
              : `${failed.length} of ${results.length} could not be done: ${failed[0]!.error ?? 'refused'}`,
          )
          return
        }
        // THE LANDING IS WHAT MAKES A MOVE UNDOABLE. Matched by op id rather
        // than by position, because the results array correlates by id and the
        // batch is independent by construction.
        for (const [index, entry] of pending.entries()) {
          const result = results.find((r) => r.id === ops[index]?.id)
          queue.current.ack(entry.id, result?.moved ? { uid: result.moved.uid } : undefined)
        }
        refreshToast()
      } catch (error) {
        // The request itself did not land, so NOTHING happened: every row goes
        // back, which is what an all-ids rollback means.
        rollback(new Set(rowIds))
        for (const entry of pending) queue.current.fail(entry.id)
        refreshToast()
        options.onError(
          error instanceof HearthError && error.code === 'not_configured'
            ? 'This server has no connection to the mail server, so nothing was changed'
            : error instanceof Error
              ? error.message
              : 'That did not work',
        )
      }
    },
    [options, refreshToast],
  )

  const flag = useCallback(
    async (rows: readonly MessageRow[], add: string[], remove: string[], label: string): Promise<void> => {
      if (rows.length === 0) return
      const before = new Map(rows.map((row) => [row.id, row.flags]))

      options.patchRows((current) =>
        current.map((row) => {
          if (!before.has(row.id)) return row
          const flags = flagsOf(row)
          for (const value of add) flags.add(value)
          for (const value of remove) flags.delete(value)
          return { ...row, flags: flagsToText(flags) }
        }),
      )

      await send(
        rows.map((row, index) => ({
          id: `f${index}-${row.uid}`,
          op: 'flag' as const,
          mailbox: row.mailbox,
          uid: row.uid,
          add,
          remove,
        })),
        rows.map((row) => ({ kind: 'flag' as const, mailbox: row.mailbox, uid: row.uid, add, remove })),
        label,
        (failedRows) =>
          options.patchRows((current) =>
            current.map((row) => (failedRows.has(row.id) ? { ...row, flags: before.get(row.id)! } : row)),
          ),
        rows.map((row) => row.id),
      )
    },
    [options, send],
  )

  /**
   * Take these rows off the screen, send the batch, and make it undoable.
   *
   * ONE IMPLEMENTATION FOR ARCHIVE AND FOR SNOOZE, because to this file they are
   * the same event: the message leaves the mailbox the query named, and the
   * inverse is a move back to where it was. The only thing that differs is the
   * op on the wire — `move` for the first, `snooze` for the second, which
   * carries a wake time as well — and the QUEUE does not need to know which,
   * because the origin answers a snooze with `moved` exactly as it answers a
   * move, and moving a parked message back is itself what ends the snooze.
   *
   * THAT IS WHY SNOOZE NEEDS NO NEW OPERATION KIND. `optimistic.ts` knows
   * `flag`, `label` and `move`; a fourth would mean a fourth `invert` arm, and
   * the inverse it would have to build is the move this already builds.
   */
  const vanish = useCallback(
    async (rows: readonly MessageRow[], target: string, wire: Op[], label: string): Promise<void> => {
      const moved = new Set(rows.map((row) => row.id))
      // THE INDEX IS PART OF THE SNAPSHOT. Reinstatement used to be
      // `[...snapshot, ...current]`, which put every restored row at the TOP of
      // a list ordered by date — so a failed archive of a three-week-old message
      // moved it to the front, where it looked like new mail. Recording where
      // each row was is what lets it go back there.
      let position = 0
      const snapshot = new Map(
        rows.map((row) => [row.id, { row: { ...row }, index: position++ }] as const),
      )
      // Gone from THIS view immediately: the message has left the mailbox the
      // query named, so leaving it on screen would be the lie.
      options.patchRows((current) => current.filter((row) => !moved.has(row.id)))

      await send(
        wire,
        rows.map((row) => ({
          kind: 'move' as const,
          mailbox: row.mailbox,
          uid: row.uid,
          to: target,
          from: row.mailbox,
        })),
        label,
        (failedRows) => options.patchRows((current) => reinstate(current, snapshot, failedRows)),
        rows.map((row) => row.id),
        (batchId) => {
          removed.current.set(batchId, { rowIds: rows.map((row) => row.id), snapshot })
          // Oldest first out, so the map is bounded by actions rather than by
          // how long the tab has been open.
          while (removed.current.size > MAX_REMEMBERED_BATCHES) {
            removed.current.delete(removed.current.keys().next().value!)
          }
        },
      )
    },
    [options, send],
  )

  const moveToSpecial = useCallback(
    async (rows: readonly MessageRow[], specialUse: string, label: string): Promise<void> => {
      if (rows.length === 0) return
      const target = specialMailbox(options.mailboxes, specialUse)
      if (!target) {
        // NAMED, NOT GUESSED. `shell-ia.md` says to show a named error state
        // when the server advertises no such SPECIAL-USE rather than falling
        // back to a literal folder name that may not exist — `UID MOVE` to a
        // nonexistent mailbox is a NO, and archiving is the most-pressed key.
        options.onError(`Your mail server advertises no ${specialUse} mailbox, so there is nowhere to put this`)
        return
      }

      await vanish(
        rows,
        target,
        rows.map((row, index) => ({
          id: `m${index}-${row.uid}`,
          op: 'move' as const,
          mailbox: row.mailbox,
          uid: row.uid,
          to: target,
        })),
        label,
      )
    },
    [options, vanish],
  )

  const moveTo = useCallback(
    async (rows: readonly MessageRow[], mailbox: string, label: string): Promise<void> => {
      if (rows.length === 0 || mailbox === '') return
      // NOTHING TO DO IS NOT AN ERROR. Picking the folder the message is already
      // in is a thing a person does; `UID MOVE` to the current mailbox is a NO
      // on some servers and a no-op on others, and neither is worth a toast.
      const elsewhere = rows.filter((row) => row.mailbox !== mailbox)
      if (elsewhere.length === 0) return
      await vanish(
        elsewhere,
        mailbox,
        elsewhere.map((row, index) => ({
          id: `v${index}-${row.uid}`,
          op: 'move' as const,
          mailbox: row.mailbox,
          uid: row.uid,
          to: mailbox,
        })),
        label,
      )
    },
    [vanish],
  )

  const label = useCallback(
    async (rows: readonly MessageRow[], add: string[], remove: string[], text: string): Promise<void> => {
      await flag(rows, add, remove, text)
    },
    [flag],
  )

  const snooze = useCallback(
    async (rows: readonly MessageRow[], until: number | null, label: string): Promise<void> => {
      if (rows.length === 0) return
      // WHERE THE ORIGIN WILL PUT IT, worked out the same way the origin works
      // it out — from the SPECIAL-USE flag on the mailbox list the origin itself
      // published. This is not the client deciding where the mail goes: the op
      // carries a GUID and a time and nothing else, and the origin resolves the
      // folder. It is needed here only so the undo queue records the move that
      // is about to happen, and if the two ever disagreed the cost would be a
      // refused Undo rather than a misplaced message.
      const target = until === null ? SNOOZE_WAKE_MAILBOX : specialMailbox(options.mailboxes, SNOOZE_SPECIAL_USE)
      if (!target) {
        options.onError(
          `Your mail server advertises no ${SNOOZE_SPECIAL_USE} mailbox, so there is nowhere to put a snoozed message`,
        )
        return
      }

      await vanish(
        rows,
        target,
        // `guid`, NOT `uid`. The wake time is a column of the message, and one
        // message can be sitting in several mailboxes at once — keying it by
        // location would let the same message be snoozed and not snoozed at the
        // same time, which `is:snoozed` would then have to guess about.
        rows.map((row, index) => ({ id: `b${index}-${row.uid}`, op: 'snooze' as const, guid: row.id, until })),
        label,
      )
    },
    [options, vanish],
  )

  const undo = useCallback(async (): Promise<void> => {
    const current = queue.current.toast()
    if (!current) return
    // THE WHOLE BATCH. This took `toast().opId` and sent one inverse, so an
    // Undo after "Archived 12 conversations" restored one message and reported
    // success — the other eleven stayed archived with nothing to say so.
    const inverses = queue.current.undoBatch(current.batchId)
    if (!inverses) {
      // Honest rather than silent: a move whose server sent no COPYUID has no
      // inverse that names the right message, and never will.
      options.onError('That cannot be undone — the mail server did not say where the message went')
      return
    }

    const ops: Op[] = inverses.map((entry, index) => {
      const operation = entry.op
      return operation.kind === 'flag'
        ? { id: `u${index}`, op: 'flag', mailbox: operation.mailbox, uid: operation.uid, add: [...operation.add], remove: [...operation.remove] }
        : operation.kind === 'move'
          ? { id: `u${index}`, op: 'move', mailbox: operation.mailbox, uid: operation.uid, to: operation.to }
          : { id: `u${index}`, op: 'label', guid: operation.guid, add: [...operation.add], remove: [...operation.remove] }
    })

    try {
      const { results } = await api.ops(ops)
      const failed = results.filter((result) => !result.ok)
      for (const [index, entry] of inverses.entries()) {
        const result = results.find((r) => r.id === ops[index]?.id)
        if (result?.ok) queue.current.ack(entry.id, result.moved ? { uid: result.moved.uid } : undefined)
        else queue.current.fail(entry.id)
      }
      if (failed.length > 0) {
        options.onError(
          failed.length === results.length
            ? (failed[0]!.error ?? 'The undo was refused')
            : `${failed.length} of ${results.length} could not be undone`,
        )
      }
      // AND THE LIST IS PUT BACK. Undo used to change the mailbox and leave the
      // screen alone, so the reader watched an Undo succeed while the rows
      // stayed gone — and only a reload showed it had worked.
      //
      // `undoBatch` returns the inverses in REVERSE order, so inverse `i`
      // belongs to original row `n - 1 - i`. Only the ones the server actually
      // restored go back.
      const taken = removed.current.get(current.batchId)
      if (taken) {
        const restored = new Set<string>()
        for (const [index, op] of ops.entries()) {
          const result = results.find((r) => r.id === op.id)
          const rowId = taken.rowIds[taken.rowIds.length - 1 - index]
          if (result?.ok && rowId) restored.add(rowId)
        }
        if (restored.size > 0) options.patchRows((rows) => reinstate(rows, taken.snapshot, restored))
        removed.current.delete(current.batchId)
      }
    } catch (error) {
      for (const entry of inverses) queue.current.fail(entry.id)
      options.onError(error instanceof Error ? error.message : 'The undo did not work')
    }
    setToast(null)
  }, [options])

  return { toast, flag, moveToSpecial, moveTo, label, snooze, undo, dismissToast: () => setToast(null) }
}
